import { useMemo } from 'react'

import { fromIsoDate, getMonthDates, monthStartIso, toIsoDate } from '../../lib/bookingCalendar'

type DaySummary = {
  availableCount: number
  enabled: boolean
  closed: boolean
  loading?: boolean
}

type BookingCalendarProps = {
  selectedDate: string
  visibleMonth: string
  onVisibleMonthChange: (value: string) => void
  onSelectDate: (value: string) => void
  daySummaries: Record<string, DaySummary>
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function shiftMonth(monthStart: string, delta: number): string {
  const date = fromIsoDate(monthStart) || new Date()
  date.setMonth(date.getMonth() + delta, 1)
  return monthStartIso(toIsoDate(date))
}

export default function BookingCalendar({
  selectedDate,
  visibleMonth,
  onVisibleMonthChange,
  onSelectDate,
  daySummaries,
}: BookingCalendarProps) {
  const monthLabel = useMemo(() => {
    const date = fromIsoDate(visibleMonth)
    if (!date) return ''
    return date.toLocaleDateString([], { month: 'long', year: 'numeric' })
  }, [visibleMonth])

  const monthDays = useMemo(() => getMonthDates(visibleMonth), [visibleMonth])
  const leadingBlanks = useMemo(() => {
    if (!monthDays.length) return 0
    const first = fromIsoDate(monthDays[0])
    if (!first) return 0
    const sundayBased = first.getDay()
    return sundayBased === 0 ? 6 : sundayBased - 1
  }, [monthDays])

  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onVisibleMonthChange(shiftMonth(visibleMonth, -1))}
          className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10"
          aria-label="Previous month"
        >
          Prev
        </button>
        <div className="text-sm font-semibold">{monthLabel}</div>
        <button
          type="button"
          onClick={() => onVisibleMonthChange(shiftMonth(visibleMonth, 1))}
          className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10"
          aria-label="Next month"
        >
          Next
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-white/55">
        {WEEKDAY_LABELS.map((label) => <div key={label}>{label}</div>)}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {Array.from({ length: leadingBlanks }).map((_, idx) => (
          <div key={`blank-${idx}`} className="h-10" />
        ))}
        {monthDays.map((iso) => {
          const date = fromIsoDate(iso)
          const day = date ? date.getDate() : 0
          const selected = iso === selectedDate
          const summary = daySummaries[iso]
          const hasAvailability = Number(summary?.availableCount || 0) > 0
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onSelectDate(iso)}
              className={`relative h-10 rounded-lg border text-sm transition ${
                selected
                  ? 'border-brand bg-brand/20 text-white'
                  : 'border-white/10 bg-white/5 text-white/85 hover:bg-white/10'
              }`}
              title={hasAvailability ? `${summary.availableCount} slot${summary.availableCount === 1 ? '' : 's'} available` : 'No slots'}
            >
              {day}
              {summary?.loading ? (
                <span className="absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-white/50" />
              ) : hasAvailability ? (
                <span className="absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-emerald-400" />
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}
